import { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import FitScreenOutlinedIcon from '@mui/icons-material/FitScreenOutlined';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import { useCameraStore } from '@/store/cameraStore';
import { useEngine } from '@/app/EngineContext';
import { centerOnSelection, zoomToContent } from '@/features/camera/cameraNavigation';
import { CAMERA_LIMITS } from '@/constants/camera';
import { IconButton } from '@/components/ui';
import styles from './Chrome.module.css';

/**
 * On-canvas camera controls. These are the primary zoom affordance on touch devices,
 * where there is no wheel or keyboard, and a convenience elsewhere. The zoom readout
 * is updated once per frame from a subscription, not per camera change.
 */
export const ViewportControls = () => {
  const { scene, frames, animator } = useEngine();
  const [zoomText, setZoomText] = useState(() => formatZoom(useCameraStore.getState().camera.zoom));

  useEffect(
    () =>
      useCameraStore.subscribe((s, prev) => {
        if (s.camera.zoom !== prev.camera.zoom) {
          frames.schedule(() => {
            setZoomText(formatZoom(useCameraStore.getState().camera.zoom));
          });
        }
      }),
    [frames],
  );

  const cam = () => useCameraStore.getState();

  return (
    <Paper
      className={styles.viewportControls}
      role="toolbar"
      aria-label="Viewport"
      data-testid="viewport-controls"
      onPointerDown={(e) => {
        e.stopPropagation();
      }}
    >
      <IconButton
        label="Zoom out"
        shortcut="⌘−"
        data-testid="zoom-out"
        onClick={() => {
          cam().zoomBy(1 / CAMERA_LIMITS.zoomStep);
        }}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      <Tooltip title="Reset zoom (⌘0)">
        <Button
          className={styles.zoomReadout}
          aria-label="Reset zoom to 100%"
          data-testid="zoom-reset"
          color="inherit"
          onClick={() => {
            cam().setZoom(1);
          }}
        >
          {zoomText}
        </Button>
      </Tooltip>
      <IconButton
        label="Zoom in"
        shortcut="⌘+"
        data-testid="zoom-in"
        onClick={() => {
          cam().zoomBy(CAMERA_LIMITS.zoomStep);
        }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
      <Divider orientation="vertical" flexItem />
      <IconButton
        label="Fit to content"
        shortcut="⇧1"
        data-testid="fit-content"
        onClick={() => {
          zoomToContent(scene, animator);
        }}
      >
        <FitScreenOutlinedIcon fontSize="small" />
      </IconButton>
      <IconButton
        label="Center on selection"
        shortcut="⇧4"
        data-testid="center-selection"
        onClick={() => {
          centerOnSelection(scene, animator);
        }}
      >
        <CenterFocusStrongOutlinedIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
};

const formatZoom = (zoom: number): string => `${Math.round(zoom * 100)}%`;
